/**
 * Order Invoice Service
 * Service for generating order invoice PDFs and printing invoices
 *
 * @author SAUBHAGYA Development Team
 * @version 1.0.0
 */

import jsPDF from 'jspdf';
import type { OrderDetails } from '@/types/order';
import orderService from './orderService';

// ============================================
// TYPES
// ============================================

interface InvoiceConfig {
  companyName: string;
  companyAddress: string;
  companyGSTIN: string;
  companyPhone: string;
  companyEmail: string;
  logoUrl?: string;
}

// ============================================
// CONFIGURATION
// ============================================

const INVOICE_CONFIG: InvoiceConfig = {
  companyName: 'SAUBHAGYA Biogas Systems',
  companyAddress: 'Industrial Area, Sector 15, Maharashtra, India - 400001',
  companyGSTIN: '27AABCS1234F1Z5',
  companyPhone: '+91-9876543210',
  companyEmail: 'info@saubhagya.com',
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format currency for PDF (uses Rs. prefix since jsPDF's built-in fonts
 * do not support the Rupee Unicode character U+20B9)
 */
function formatCurrency(amount: number): string {
  return `Rs.${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format date in dd-MM-yyyy format
 */
function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Get payment status label
 */
function getPaymentStatusLabel(status: string): string {
  const statusMap: Record<string, string> = {
    PENDING: 'Pending',
    PARTIAL: 'Partially Paid',
    PAID: 'Fully Paid',
    REFUNDED: 'Refunded',
    FAILED: 'Failed',
  };
  return statusMap[status] || status;
}

// ============================================
// PDF GENERATION
// ============================================

/**
 * Generate invoice PDF
 */
function generateInvoicePDF(order: OrderDetails): jsPDF {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let yPosition = 20;
  const leftMargin = 15;
  const rightMargin = pageWidth - 15;
  const contentWidth = pageWidth - 30;

  // ============================================
  // HEADER SECTION
  // ============================================

  // Company Name
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(INVOICE_CONFIG.companyName, leftMargin, yPosition);
  yPosition += 6;

  // Company Details
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(INVOICE_CONFIG.companyAddress, leftMargin, yPosition);
  yPosition += 4;
  doc.text(`GSTIN: ${INVOICE_CONFIG.companyGSTIN}`, leftMargin, yPosition);
  yPosition += 4;
  doc.text(`Phone: ${INVOICE_CONFIG.companyPhone} | Email: ${INVOICE_CONFIG.companyEmail}`, leftMargin, yPosition);
  yPosition += 10;

  // Invoice Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('TAX INVOICE', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 10;

  // Horizontal line
  doc.setLineWidth(0.5);
  doc.line(leftMargin, yPosition, rightMargin, yPosition);
  yPosition += 8;

  // ============================================
  // ORDER HEADER SECTION
  // ============================================

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  // Left column - Order details
  const leftCol = leftMargin;
  const rightCol = pageWidth / 2 + 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Order Number:', leftCol, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(order.orderNumber, leftCol + 35, yPosition);

  doc.setFont('helvetica', 'bold');
  doc.text('Invoice Date:', rightCol, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(order.orderDate), rightCol + 30, yPosition);
  yPosition += 5;

  doc.setFont('helvetica', 'bold');
  doc.text('Order Date:', leftCol, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(order.orderDate), leftCol + 35, yPosition);

  doc.setFont('helvetica', 'bold');
  doc.text('Status:', rightCol, yPosition);
  doc.setFont('helvetica', 'normal');
  doc.text(order.orderStatus, rightCol + 30, yPosition);
  yPosition += 10;

  // ============================================
  // CUSTOMER INFORMATION SECTION
  // ============================================

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Bill To:', leftCol, yPosition);
  yPosition += 5;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(order.customer.customerName, leftCol, yPosition);
  yPosition += 5;

  doc.setFont('helvetica', 'normal');
  doc.text(`Email: ${order.customer.email}`, leftCol, yPosition);
  yPosition += 4;
  doc.text(`Phone: ${order.customer.phone}`, leftCol, yPosition);
  yPosition += 4;

  if (order.customer.gstNumber) {
    doc.text(`GST Number: ${order.customer.gstNumber}`, leftCol, yPosition);
    yPosition += 4;
  }

  yPosition += 6;

  // ============================================
  // DELIVERY INFORMATION SECTION
  // ============================================

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Delivery Address:', leftCol, yPosition);
  yPosition += 5;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(order.delivery.address.addressLine1, leftCol, yPosition);
  yPosition += 4;

  if (order.delivery.address.addressLine2) {
    doc.text(order.delivery.address.addressLine2, leftCol, yPosition);
    yPosition += 4;
  }

  doc.text(`${order.delivery.address.city}, ${order.delivery.address.state} - ${order.delivery.address.pincode}`, leftCol, yPosition);
  yPosition += 4;
  doc.text(order.delivery.address.country, leftCol, yPosition);
  yPosition += 4;

  doc.text(`Contact: ${order.delivery.contactPerson} (${order.delivery.contactPhone})`, leftCol, yPosition);
  yPosition += 4;

  doc.text(`Expected Delivery: ${formatDate(order.delivery.expectedDeliveryDate)}`, leftCol, yPosition);
  yPosition += 10;

  // ============================================
  // LINE ITEMS TABLE
  // ============================================

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Line Items:', leftCol, yPosition);
  yPosition += 6;

  // Table header
  const tableStartY = yPosition;
  const colWidths = {
    sno: 10,
    product: 55,
    qty: 25,
    price: 30,
    discount: 25,
    tax: 25,
    total: 30,
  };

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(240, 240, 240);
  doc.rect(leftMargin, yPosition, contentWidth, 7, 'F');

  let xPos = leftMargin + 2;
  doc.text('S.No', xPos, yPosition + 5);
  xPos += colWidths.sno;
  doc.text('Product', xPos, yPosition + 5);
  xPos += colWidths.product;
  doc.text('Quantity', xPos, yPosition + 5);
  xPos += colWidths.qty;
  doc.text('Unit Price', xPos, yPosition + 5);
  xPos += colWidths.price;
  doc.text('Discount', xPos, yPosition + 5);
  xPos += colWidths.discount;
  doc.text('Tax', xPos, yPosition + 5);
  xPos += colWidths.tax;
  doc.text('Total', xPos, yPosition + 5);

  yPosition += 7;

  // Table rows
  doc.setFont('helvetica', 'normal');
  order.lineItems.forEach((item, index) => {
    // Check if we need a new page
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = 20;
    }

    xPos = leftMargin + 2;
    doc.text(`${index + 1}`, xPos, yPosition + 5);
    xPos += colWidths.sno;

    // Wrap product name if too long
    const productName = item.productName.length > 30 ? item.productName.substring(0, 27) + '...' : item.productName;
    doc.text(productName, xPos, yPosition + 5);
    xPos += colWidths.product;

    doc.text(`${item.quantity} ${item.quantityUnit}`, xPos, yPosition + 5);
    xPos += colWidths.qty;

    doc.text(formatCurrency(item.unitPrice), xPos, yPosition + 5);
    xPos += colWidths.price;

    doc.text(formatCurrency(item.discountAmount), xPos, yPosition + 5);
    xPos += colWidths.discount;

    doc.text(formatCurrency(item.totalTax), xPos, yPosition + 5);
    xPos += colWidths.tax;

    doc.text(formatCurrency(item.lineTotal), xPos, yPosition + 5);

    yPosition += 7;
  });

  // Table border
  doc.setLineWidth(0.3);
  doc.rect(leftMargin, tableStartY, contentWidth, yPosition - tableStartY);

  yPosition += 8;

  // ============================================
  // TAX BREAKDOWN SECTION
  // ============================================

  const summaryStartX = pageWidth - 80;
  const labelWidth = 40;
  const valueWidth = 30;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  // Subtotal
  doc.text('Subtotal:', summaryStartX, yPosition);
  doc.text(formatCurrency(order.gstBreakdown.subtotal), summaryStartX + labelWidth, yPosition, { align: 'right' });
  yPosition += 5;

  // Discount
  if (order.gstBreakdown.totalDiscount > 0) {
    doc.text('Discount:', summaryStartX, yPosition);
    doc.text(`- ${formatCurrency(order.gstBreakdown.totalDiscount)}`, summaryStartX + labelWidth, yPosition, { align: 'right' });
    yPosition += 5;
  }

  // CGST
  if (order.gstBreakdown.cgstAmount > 0) {
    doc.text('CGST (9%):', summaryStartX, yPosition);
    doc.text(formatCurrency(order.gstBreakdown.cgstAmount), summaryStartX + labelWidth, yPosition, { align: 'right' });
    yPosition += 5;
  }

  // SGST
  if (order.gstBreakdown.sgstAmount > 0) {
    doc.text('SGST (9%):', summaryStartX, yPosition);
    doc.text(formatCurrency(order.gstBreakdown.sgstAmount), summaryStartX + labelWidth, yPosition, { align: 'right' });
    yPosition += 5;
  }

  // IGST
  if (order.gstBreakdown.igstAmount > 0) {
    doc.text('IGST (18%):', summaryStartX, yPosition);
    doc.text(formatCurrency(order.gstBreakdown.igstAmount), summaryStartX + labelWidth, yPosition, { align: 'right' });
    yPosition += 5;
  }

  // Total Tax
  doc.setFont('helvetica', 'bold');
  doc.text('Total Tax:', summaryStartX, yPosition);
  doc.text(formatCurrency(order.gstBreakdown.totalTax), summaryStartX + labelWidth, yPosition, { align: 'right' });
  yPosition += 7;

  // Total Amount
  doc.setFontSize(12);
  doc.setFillColor(240, 240, 240);
  doc.rect(summaryStartX - 2, yPosition - 5, labelWidth + valueWidth + 4, 8, 'F');
  doc.text('Total Amount:', summaryStartX, yPosition);
  doc.text(formatCurrency(order.gstBreakdown.totalAmount), summaryStartX + labelWidth, yPosition, { align: 'right' });
  yPosition += 10;

  // ============================================
  // PAYMENT INFORMATION SECTION
  // ============================================

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Information:', leftMargin, yPosition);
  yPosition += 5;

  doc.setFont('helvetica', 'normal');
  doc.text(`Total Amount: ${formatCurrency(order.payment.totalAmount)}`, leftMargin, yPosition);
  yPosition += 5;
  doc.text(`Paid Amount: ${formatCurrency(order.payment.paidAmount)}`, leftMargin, yPosition);
  yPosition += 5;
  doc.text(`Balance Due: ${formatCurrency(order.payment.balanceDue)}`, leftMargin, yPosition);
  yPosition += 5;
  doc.text(`Payment Status: ${getPaymentStatusLabel(order.payment.paymentStatus)}`, leftMargin, yPosition);
  yPosition += 10;

  // ============================================
  // FOOTER SECTION
  // ============================================

  // Special Instructions
  if (order.delivery.specialInstructions) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Special Instructions:', leftMargin, yPosition);
    yPosition += 4;
    doc.setFont('helvetica', 'normal');
    const instructions = doc.splitTextToSize(order.delivery.specialInstructions, contentWidth - 10);
    doc.text(instructions, leftMargin, yPosition);
    yPosition += (instructions.length * 4) + 6;
  }

  // Terms and Conditions
  const footerY = pageHeight - 20;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('Terms & Conditions: This is a computer-generated invoice and does not require a signature.', leftMargin, footerY);
  doc.text('Payment should be made within the agreed terms. For any queries, please contact us.', leftMargin, footerY + 4);

  return doc;
}

// ============================================
// SERVICE METHODS
// ============================================

/**
 * Order Invoice Service
 */
export const orderInvoiceService = {
  /**
   * Get invoice data for an order
   */
  async getInvoiceData(orderId: number): Promise<OrderDetails> {
    try {
      return await orderService.getOrderById(orderId);
    } catch (error) {
      console.error('[OrderInvoiceService] Error fetching invoice data:', error);
      throw new Error('Failed to fetch invoice data. Please try again.');
    }
  },

  /**
   * Generate and download invoice PDF
   */
  generateInvoicePDF(order: OrderDetails): void {
    try {
      const doc = generateInvoicePDF(order);
      const fileName = `Invoice_${order.orderNumber}_${formatDate(order.orderDate)}.pdf`;
      doc.save(fileName);
      console.log(`[OrderInvoiceService] Invoice PDF generated: ${fileName}`);
    } catch (error) {
      console.error('[OrderInvoiceService] Error generating invoice PDF:', error);
      throw new Error('Failed to generate invoice PDF. Please try again.');
    }
  },

  /**
   * Print invoice
   */
  printInvoice(order: OrderDetails): void {
    try {
      const doc = generateInvoicePDF(order);

      // Open PDF in new window for printing
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          // Clean up the URL after a delay
          setTimeout(() => {
            URL.revokeObjectURL(pdfUrl);
          }, 1000);
        };
      } else {
        throw new Error('Unable to open print window. Please check your browser settings.');
      }

      console.log('[OrderInvoiceService] Invoice sent to printer');
    } catch (error) {
      console.error('[OrderInvoiceService] Error printing invoice:', error);
      throw new Error('Failed to print invoice. Please try again.');
    }
  },
};

export default orderInvoiceService;
